import { ChangeEvent, useEffect, useState } from 'react';
import { useRequestPayoutApprovalMutation } from 'api/approval';
import { useGetErpConfigsQuery } from 'api/integration';
import { useExecutePayoutMutation, useGetQuoteFiatQuery, useMakePayoutRequestMutation } from 'api/moveMoney';
import { FLOW_PERMISSION_PROPERTIES } from 'constants/accessConstants';
import { INFO_ROUND } from 'constants/icons';
import { defaultFn, SESSION_CAPABILITY_CONTEXT_KEYS } from 'constants/index';
import { TOTP_ERRORS } from 'constants/mfa';
import { DONT_SYNC } from 'constants/transfer';
import { Button, BUTTON_SIZE_TYPES, BUTTON_STATE_TYPES, BUTTON_TYPES, Dropdown, SvgSpriteLoader, Text } from 'destiny';
import { ICON_SPRITE_TYPES } from 'destiny/dist/constants/icons';
import { OptionsType } from 'destiny/dist/types/dropdown';
import { useIsSuperUser } from 'hooks';
import { useAppSelector } from 'hooks/toolkit';
import useConfiguration, { CONFIG_DEFINITIONS } from 'hooks/useConfiguration';
import { useSessionToken } from 'hooks/useSessionToken';
import useUserAccessFromPermission from 'hooks/useUserAccessFromPermission';
import CheckMFA from 'modules/settings/CheckMFA';
import AmountDetails from 'modules/single-transfer/fiat/AmountDetails';
import RfqSummarySkeleton from 'modules/single-transfer/thirdPartyPayouts/steps/skeleton/RfqSummarySkeleton';
import Image from 'next/image';
import { RootState } from 'store';
import { ApiErrorResponse } from 'types/api';
import { PendingPayoutTypes } from 'types/approval';
import { ThirdPartyReviewAndPayProps } from 'types/transactions';
import { trackMixpanel } from 'utils/mixpanel';
import { ErrorCardTypes } from 'components/banners/types';
import { ButtonLoader } from 'components/loader/ButtonLoader';
import SkeletonElement from 'components/skeletons/SkeletonElement';
import { SkeletonTypes } from 'components/skeletons/types';
import Textarea from 'components/textarea/Textarea';
import CommonWrapper from 'components/wrappers/CommonWrapper';

const ReviewAndPay: React.FC<ThirdPartyReviewAndPayProps> = ({
  selectedSourceAccount,
  selectedSourceCurrency,
  selectedDestCurrency,
  quotePayload,
  quoteId = '',
  isQuoteAutoUpdated = false,
  onSuccess = defaultFn,
  onError = defaultFn,
  handleBack = defaultFn,
}) => {
  const isSuperUser = useIsSuperUser();
  const { checkConfigEnabled } = useConfiguration();

  const isPoliciesEnabled = checkConfigEnabled(CONFIG_DEFINITIONS.POLICIES_ENABLED);

  const [totp, setTotp] = useState('');
  const [totpError, setTotpError] = useState('');
  const [comment, setComment] = useState<string>('');
  const [showCommentBox, setShowCommentBox] = useState<boolean>(false);
  const [selectedChartOfAccounts, setSelectedChartOfAccounts] = useState<OptionsType>();
  const [chartOfAccountsList, setChartOfAccountsList] = useState<OptionsType[]>();

  const { currencyCodeAndNameMap } = useAppSelector((state: RootState) => state.config);
  const { user: userData } = useAppSelector((state: RootState) => state?.user);

  const { data, isLoading, isError, refetch } = useGetQuoteFiatQuery({ quoteId }, { skip: !quoteId });
  const {
    data: erpConfigs,
    isLoading: isErpConfigsLoading,
    isError: erpConfigsError,
  } = useGetErpConfigsQuery(
    { vault_id: selectedSourceAccount?.id || '' },
    { skip: !selectedSourceAccount?.id || !isSuperUser }
  );
  const [executePayment, { isLoading: isInitiateLoading }] = useExecutePayoutMutation();
  const [requestPayoutApproval, { isLoading: isRequestLoading }] = useRequestPayoutApprovalMutation();
  const [makePayoutRequest, { isLoading: isRequestPayoutLoading }] = useMakePayoutRequestMutation();
  const { sessionToken, createSessionToken } = useSessionToken(
    SESSION_CAPABILITY_CONTEXT_KEYS.REQUEST_PAYOUTS_TRANSACTION
  );

  const isMakerRole = !useUserAccessFromPermission(
    FLOW_PERMISSION_PROPERTIES.UPDATE_PAYOUT_REQUESTS.permissionId,
    FLOW_PERMISSION_PROPERTIES.UPDATE_PAYOUT_REQUESTS.scope
  );

  const isExecutePaymentDisabled =
    !userData?.is_mfa_enabled ||
    totp?.trim()?.length !== 6 ||
    isLoading ||
    (erpConfigs?.is_chart_of_accounts_required && !selectedChartOfAccounts);

  useEffect(() => {
    if (erpConfigs?.chart_of_accounts?.length && !chartOfAccountsList) {
      const list = erpConfigs?.chart_of_accounts ?? [];

      setChartOfAccountsList([{ label: 'Do not sync', value: DONT_SYNC }, ...list]);
    }
  }, [erpConfigs]);

  const handleError = (e: ApiErrorResponse) => {
    if (e?.data?.error) {
      const { code, message } = e.data.error;

      if (code === TOTP_ERRORS.INVALID_TOTP) setTotpError(message);
      else onError(e);
    } else {
      onError();
    }
  };

  const onSubmit = () => {
    if (!isExecutePaymentDisabled) {
      if (isSuperUser || isPoliciesEnabled) return handleRequestPayout();
      if (isMakerRole) return handleRequest();
      else return handleSubmit();
    }
  };

  const handleRequestPayout = () => {
    const chart_of_accounts_id = selectedChartOfAccounts?.value;
    const shouldSync = erpConfigsError ? false : !!chart_of_accounts_id && chart_of_accounts_id !== DONT_SYNC;

    makePayoutRequest({
      data: {
        type: PendingPayoutTypes.SINGLE_PAYOUT,
        request_details: JSON.stringify({
          ...quotePayload,
          comments: comment,
          should_sync: shouldSync,
          chart_of_accounts_id: shouldSync ? chart_of_accounts_id : undefined,
          computed_source_amount: data?.source_amount,
        }),
      },
      accountId: selectedSourceAccount?.id,
      totp: totp,
      sessionToken: sessionToken ?? '',
    })
      .unwrap()
      .then(() => {
        onSuccess({
          amount: data?.source_amount,
          currency: selectedSourceCurrency ? currencyCodeAndNameMap[selectedSourceCurrency?.code] : '',
        });
      })
      .catch((e) => {
        handleError(e);
      })
      .finally(() => {
        createSessionToken();
      });
  };

  const handleSubmit = () => {
    const chart_of_accounts_id = selectedChartOfAccounts?.value;
    const shouldSync = erpConfigsError ? false : !!chart_of_accounts_id && chart_of_accounts_id !== DONT_SYNC;

    executePayment({
      data: {
        quote_id: quoteId,
        comments: comment,
        should_sync: shouldSync,
        chart_of_accounts_id: shouldSync ? chart_of_accounts_id : undefined,
      },
      accountId: selectedSourceAccount?.id,
      headerKey: totp,
    })
      .unwrap()
      .then(() => {
        onSuccess({
          amount: data?.source_amount,
          currency: selectedSourceCurrency ? currencyCodeAndNameMap[selectedSourceCurrency?.code] : '',
        });
      })
      .catch((e) => {
        handleError(e);
      });
  };

  const handleRequest = () => {
    const chart_of_accounts_id = selectedChartOfAccounts?.value;
    const shouldSync = erpConfigsError ? false : !!chart_of_accounts_id;

    requestPayoutApproval({
      type: PendingPayoutTypes.SINGLE_PAYOUT,
      accountId: selectedSourceAccount?.id,
      requestDetails: JSON.stringify({
        ...quotePayload,
        comments: comment,
        should_sync: shouldSync,
        chart_of_accounts_id: shouldSync ? chart_of_accounts_id : undefined,
      }),
      headerKey: totp,
    })
      .unwrap()
      .then(() => {
        onSuccess({
          amount: data?.source_amount,
          currency: selectedSourceCurrency ? currencyCodeAndNameMap[selectedSourceCurrency?.code] : '',
        });
      })
      .catch((e) => {
        handleError(e);
      });
  };

  const handleSetTotp = (value: string) => {
    setTotp(value);
  };

  const handleShowCommentBox = () => {
    setShowCommentBox((prevValue) => !prevValue);
  };

  const handleAddComment = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (e?.target) setComment(e?.target?.value);
  };

  const handleSetSelectedChartOfAccounts = (selectedValue: OptionsType) => setSelectedChartOfAccounts(selectedValue);

  return (
    <div className='tw-mt-6'>
      <CommonWrapper
        isLoading={isLoading}
        skeletonType={SkeletonTypes.CUSTOM}
        skeleton={<RfqSummarySkeleton isQuoteAutoUpdated={isQuoteAutoUpdated} />}
        isError={isError}
        errorCardType={ErrorCardTypes.API_FAIL}
        refetchFunnction={refetch}
      >
        <>
          <div className='tw-mb-3'>
            <div className='tw-flex tw-items-center tw-justify-between tw-mb-3.5'>
              <div className='tw-text-TEXT_PRIMARY f-12-300'>You are sending</div>
              <div className='tw-text-TEXT_BLACK_1 f-16-400'>{`${data?.source_amount} ${
                selectedSourceCurrency ? currencyCodeAndNameMap[selectedSourceCurrency?.code] : ''
              }`}</div>
            </div>

            {selectedSourceCurrency && selectedDestCurrency && (
              <AmountDetails
                transferDetails={data}
                corridor={selectedSourceCurrency?.corridor_type}
                destinationCurrency={currencyCodeAndNameMap?.[selectedDestCurrency?.code]}
                sourceCurr={currencyCodeAndNameMap?.[selectedSourceCurrency?.code]}
                className='tw-mb-1'
                itemsParentWrapperClassName='tw-flex-row-reverse tw-justify-between tw-mb-2'
                itemsWrapperClassName='tw-justify-end'
                currencyTextClassName='tw-text-TEXT_SECONDARY f-12-300'
                labelClassName='tw-text-TEXT_TERTIARY f-12-300'
                showIcon={false}
              />
            )}

            <div className='tw-flex tw-items-center tw-justify-between'>
              <div className='tw-text-TEXT_PRIMARY f-12-300'>Recipient gets</div>
              <div className='tw-text-TEXT_BLACK_1 f-16-400'>{`${data?.receiving_amount} ${
                selectedDestCurrency ? currencyCodeAndNameMap?.[selectedDestCurrency?.code] : ''
              }`}</div>
            </div>
          </div>

          {isQuoteAutoUpdated && (
            <div className='tw-flex tw-justify-between tw-px-4 tw-py-3 tw-bg-BASE_PRIMARY tw-rounded-2.5 tw-mb-3'>
              <div className='tw-shrink-0 tw-relative tw-mr-4'>
                <Image src={INFO_ROUND} alt='info' width={13} height={13} />
              </div>
              <div className='f-12-300 tw-text-TEXT_SECONDARY'>
                The exchange rate has been updated as it has been over 2:00 minutes. Please review the updated amount
                before proceeding
              </div>
            </div>
          )}
        </>
      </CommonWrapper>

      <div className='tw-mt-3 tw-mb-6'>
        <div
          className='tw-flex tw-items-center tw-py-2 tw-cursor-pointer'
          role='presentation'
          onClick={handleShowCommentBox}
        >
          <SvgSpriteLoader
            id={showCommentBox ? 'minus-circle' : 'plus-circle'}
            iconCategory={ICON_SPRITE_TYPES.GENERAL}
            width={16}
            height={16}
            className='tw-mr-2.5'
          />
          <Text textClass='tw-text-TEXT_PRIMARY f-14-300 tw-select-none'>Anything you’d like to add?</Text>
        </div>
        {showCommentBox && (
          <Textarea
            id='comments'
            name='text'
            value={comment}
            placeHolder='Add any notes for future reference...'
            textAreaStyle='tw-bg-white tw-h-[60px] tw-px-6 tw-py-[21px] f-14-300 tw-text-TEXT_PRIMARY tw-rounded-[5px] placeholder:tw-text-TEXT_TERTIARY placeholder:tw-text-sm placeholder:tw-font-light placeholder:tw-tracking-[0.03em] focus:tw-border-ZAMP_PRIMARY focus:tw-outline-0'
            onChange={handleAddComment}
            autoFocus={true}
          />
        )}
      </div>

      {isSuperUser && (
        <CommonWrapper
          isLoading={isErpConfigsLoading}
          skeletonType={SkeletonTypes.CUSTOM}
          skeleton={
            <div className='tw-flex tw-items-center tw-gap-2.5 tw-mb-6'>
              <SkeletonElement className='tw-w-[18px] tw-h-[18px]' />
              <SkeletonElement className='tw-w-[100px] tw-h-4' />
            </div>
          }
          isNoData={
            !erpConfigs?.is_chart_of_accounts_required ||
            !chartOfAccountsList ||
            chartOfAccountsList?.length === 0 ||
            erpConfigsError
          }
        >
          <Dropdown
            onChange={handleSetSelectedChartOfAccounts}
            showLabel
            labelProps={{
              showTitle: true,
              title: erpConfigs?.title,
              titleClass: 'tw-text-TEXT_SECONDARY f-12-300',
            }}
            options={chartOfAccountsList ?? []}
            placeholder='Select GL code'
            id='SINGLE_TRANSFER_COA_DROPDOWN'
            eventCallback={trackMixpanel}
            wrapperClass='tw-mb-6'
            customStyles={{
              control: {
                padding: '0px 24px',
                minHeight: '44px',
                height: '44px',
                backgroundColor: 'white',
              },
              valueContainer: {
                minHeight: 'unset',
              },
            }}
          />
        </CommonWrapper>
      )}

      <CheckMFA
        wrapperClassName=''
        setOtp={handleSetTotp}
        onOtpSubmit={onSubmit}
        otpError={totpError}
        autoFocus={false}
        showDivider={false}
        labelStyle='tw-text-TEXT_SECONDARY f-12-300 tw-mb-3'
        otpInputProps={{
          wrapperClassName: 'tw-gap-4',
          customStyles: 'tw-w-[52px] tw-h-[52px]',
        }}
        enableDescription='Before you proceed, add an extra layer of protection used to ensure the security'
        customEnableMFAStyles={{
          enableWrapperClass: 'tw-p-4',
          enableIconWrapperClass: 'tw-min-w-[80px] tw-min-h-[86px] tw-mr-8',
          enableIconWidth: 80,
          enableIconHeight: 86,
          enableLabelClass: 'f-14-400 tw-mb-2.5',
          enableDescriptionClass: 'f-12-300 tw-mb-2.5',
          enableButtonClass:
            'tw-bg-white tw-rounded-[100px] tw-border hover:tw-border-TEXT_PRIMARY !tw-h-[35px] tw-py-[9px] tw-px-4 f-12-400',
        }}
      />

      <div className='tw-flex tw-gap-4 tw-mt-6'>
        <Button
          buttonProps={{
            btnType: BUTTON_TYPES.SECONDARY,
            size: BUTTON_SIZE_TYPES.MEDIUM,
            state: BUTTON_STATE_TYPES.DEFAULT,
            wrapperClass: 'tw-min-w-[120px]',
            onClick: handleBack,
            id: 'MOVE_MONEY_SINGLE_TRANSFER_REVIEW_SECONDARY_BUTTON',
            eventCallback: trackMixpanel,
          }}
        >
          Back
        </Button>

        <Button
          buttonProps={{
            btnType: BUTTON_TYPES.PRIMARY,
            size: BUTTON_SIZE_TYPES.MEDIUM,
            state: BUTTON_STATE_TYPES.DEFAULT,
            wrapperClass: 'tw-min-w-[120px]',
            isLoading: isInitiateLoading || isRequestLoading || isRequestPayoutLoading,
            disabled: isExecutePaymentDisabled,
            id: 'MOVE_MONEY_SINGLE_TRANSFER_REVIEW_PRIMARY_BUTTON',
            eventCallback: trackMixpanel,
            onClick: onSubmit,
          }}
          loader={<ButtonLoader />}
        >
          {isMakerRole ? 'Send for approval' : 'Pay'}
        </Button>
      </div>
    </div>
  );
};

export default ReviewAndPay;
